import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { Box, Button, Switch, FormControlLabel } from '@mui/material';
import LabeledSelectField from '@src/components/CustomSelect/LabeledSelectField';
import { FooterAction, FormHeader, FormSection } from '.';
import { FormikProps } from 'formik';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import React, { useState, useEffect } from 'react';
import AutoComplete from '@src/components/CustomSelect/AutoComplete';
import { useNavigate } from 'react-router-dom';

interface FormOneProps {
	formik: FormikProps<any>;
	getErrorText: (field: string) => string | undefined;
	goToNextPage: () => void;
}
function FormOne({ formik, getErrorText, goToNextPage }: FormOneProps) {
	const { apiService } = React.useContext(ApplicationContext);
	const navigate = useNavigate();

	const [states, setStates] = useState([]);
	const [districts, setDistricts] = useState([]);
	const [towns, setTowns] = useState([]);
	const [DRPS, setDRPS] = useState([]);

	function getDRPS() {
		apiService?.getAllSuppliersList(
			(res) => {
				const drps = res?.data?.data?.suppliers?.map((drp: any) => ({
					id: drp.id,
					label: drp.name,
					value: drp.name,
				}));
				setDRPS(drps);
			},
			(err) => {
				console.log(err);
			},
			''
		);
	}

	function checkIfFormOneFilled() {
		return (
			formik.values.customerName &&
			formik.values.mobileNumber &&
			formik.values.state &&
			formik.values.district &&
			formik.values.town &&
			formik.values.pincode &&
			formik.values.address_1 &&
			formik.values.address_2 &&
			formik.values.preferredDRP
		);
	}

	function validateFormOne() {
		const errors = formik.errors;
		if (errors.customerName) {
			return false;
		}
		if (errors.mobileNumber) {
			return false;
		}
		if (errors.state) {
			return false;
		}
		if (errors.district) {
			return false;
		}
		if (errors.town) {
			return false;
		}
		if (errors.pincode) {
			return false;
		}
		if (errors.isCompany && !formik.values.businessName) {
			return false;
		}
		if (errors.preferredDRP) {
			return false;
		}
		return true;
	}

	useEffect(() => {
		apiService?.getStatesList(
			(res) => {
				console.log(res);
				const states = res.data.data.states.map((state: any) => ({
					id: state.state_id,
					value: state.name,
				}));
				setStates(states);
			},
			(err) => {
				console.log(err);
			},
			''
		);

		getDRPS();
	}, []);

	useEffect(() => {
		if (formik.values.state) {
			setDistricts([]);
			setTowns([]);
			apiService?.getDistrictsList(
				(res) => {
					const districts = res.data.data.districts.map((district: any) => ({
						id: district.district_id,
						value: district.name,
					}));
					setDistricts(districts);
				},
				(err) => {
					console.log(err);
				},
				formik.values.state
			);
		}
	}, [formik.values.state]);

	useEffect(() => {
		if (formik.values.district) {
			setTowns([]);
			apiService?.getCitiesList(
				(res) => {
					console.log(res);
					const towns = res.data.data.cities.map((town: any) => ({
						id: town.city_id,
						value: town.city_name,
						pincode: town.pincode,
					}));
					setTowns(towns);
				},
				(err) => {
					console.log(err);
				},
				formik.values.district
			);
		}
	}, [formik.values.district]);

	return (
		<FormSection>
			<FormHeader title="Registration" onBackClick={() => navigate('/')} />
			<Box display="flex" flexDirection="column" gap={2}>
				<LabeledTextField
					label="Customer Name"
					value={formik.values.customerName}
					onChange={formik.handleChange}
					name="customerName"
					error={Boolean(formik.touched.customerName && formik.errors.customerName)}
					errorText={getErrorText('customerName')}
					required
					onBlur={formik.handleBlur}
					styles={{ width: '100%' }}
				/>
				<LabeledTextField
					label="Mobile Number"
					value={formik.values.mobileNumber}
					onChange={formik.handleChange}
					name="mobileNumber"
					error={Boolean(formik.touched.mobileNumber && formik.errors.mobileNumber)}
					errorText={getErrorText('mobileNumber')}
					required
					type="phoneNumber"
					onBlur={formik.handleBlur}
					styles={{ width: '100%' }}
				/>
				<AutoComplete
					selectedOption={states?.find((option) => option.id === formik.values.state) || null}
					options={states || []}
					onChange={(newValue) => {
						formik.setFieldValue('state', newValue?.id || '');
						formik.setFieldValue('district', '');
						formik.setFieldValue('town', '');
						formik.setFieldValue('pincode', '');
					}}
					label="State"
					required
					error={Boolean(formik.touched.state && formik.errors.state)}
					errorText={getErrorText('state')}
				/>
				<AutoComplete
					selectedOption={districts?.find((option) => option.id === formik.values.district) || null}
					options={districts || []}
					onChange={(newValue) => {
						formik.setFieldValue('district', newValue?.id || '');
						formik.setFieldValue('town', '');
						formik.setFieldValue('pincode', '');
					}}
					label="District"
					required
					error={Boolean(formik.touched.district && formik.errors.district)}
					errorText={getErrorText('district')}
				/>
				<AutoComplete
					selectedOption={towns?.find((option) => option.id === formik.values.town) || null}
					options={towns || []}
					onChange={(newValue) => {
						formik.setFieldValue('town', newValue?.id || '');
						const pincode = towns?.find((town) => town.id === newValue?.id)?.pincode || '';
						formik.setFieldValue('pincode', pincode);
					}}
					label="Town"
					required
					error={Boolean(formik.touched.town && formik.errors.town)}
					errorText={getErrorText('town')}
				/>

				<LabeledTextField
					label="Pincode"
					value={formik.values.pincode}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						formik.setFieldValue('pincode', e.target.value);
					}}
					name="pincode"
					error={Boolean(formik.touched.pincode && formik.errors.pincode)}
					errorText={getErrorText('pincode')}
					onBlur={formik.handleBlur}
					styles={{ width: '100%' }}
				/>
				<LabeledTextField
					label="Address Line 1"
					value={formik.values.address_1}
					onChange={formik.handleChange}
					name="address_1"
					error={Boolean(formik.touched.address_1 && formik.errors.address_1)}
					errorText={getErrorText('address_1')}
					onBlur={formik.handleBlur}
					styles={{ width: '100%' }}
				/>
				<LabeledTextField
					label="Address Line 2"
					value={formik.values.address_2}
					onChange={formik.handleChange}
					name="address_2"
					error={Boolean(formik.touched.address_2 && formik.errors.address_2)}
					errorText={getErrorText('address_2')}
					onBlur={formik.handleBlur}
					styles={{ width: '100%' }}
				/>
				<FormControlLabel
					control={
						<Switch
							checked={formik.values.isCompany}
							onChange={(e) => formik.setFieldValue('isCompany', e.target.checked)}
							color="primary"
						/>
					}
					label="Are you registering as a Company?"
				/>

				{formik.values.isCompany && (
					<LabeledTextField
						label="Business Name"
						value={formik.values.businessName}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							formik.setFieldValue('businessName', e.target.value);
						}}
						name="businessName"
						error={Boolean(formik.touched.businessName && formik.errors.businessName)}
						errorText={getErrorText('businessName')}
						required
						styles={{ width: '100%' }}
					/>
				)}
				<AutoComplete
					selectedOption={DRPS?.find((option) => option.id === formik.values.preferredDRP) || null}
					options={DRPS || []}
					onChange={(newValue) => {
						formik.setFieldValue('preferredDRP', newValue?.id || '');
					}}
					label="Preferred DRP"
					required
					error={Boolean(formik.touched.preferredDRP && formik.errors.preferredDRP)}
					errorText={getErrorText('preferredDRP')}
				/>

				<Box mt={{ xs: 20, md: 0 }}></Box>

				<FooterAction>
					<Button
						variant="contained"
						color="primary"
						onClick={() => {
							formik.handleSubmit();
						}}
						// disabled={!checkIfFormOneFilled() || !validateFormOne()}
						fullWidth
					>
						Sign Up
					</Button>
				</FooterAction>
			</Box>
		</FormSection>
	);
}

export default FormOne;
